import EmptyState from '@/components/common/EmptyState';
import FilterChip from '@/components/common/FilterChip';
import GlassCard from '@/components/GlassCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import { Heading } from '@/components/Typography';
import AppLayout from '@/layouts/AppLayout';
import { FlashcardSetSummary } from '@/types';
import { usePage } from '@inertiajs/react';
import { GraduationCap, Layers } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    sets?: FlashcardSetSummary[];
    levels?: string[];
    themes?: string[];
}

type Filter =
    | { type: 'all' }
    | { type: 'level'; value: string }
    | { type: 'theme'; value: string };

const IndexContent: React.FC = () => {
    const { t } = useTranslation();
    const { props } = usePage<Props>();
    const { sets = [], levels = [], themes = [] } = props;

    const [filter, setFilter] = useState<Filter>({ type: 'all' });

    const filteredSets = useMemo(() => {
        if (filter.type === 'level') {
            return sets.filter((set) => set.level === filter.value);
        }
        if (filter.type === 'theme') {
            return sets.filter((set) => set.theme === filter.value);
        }
        return sets;
    }, [sets, filter]);

    const isActive = (candidate: Filter) =>
        candidate.type === filter.type &&
        (candidate.type === 'all' ||
            ('value' in filter &&
                'value' in candidate &&
                filter.value === candidate.value));

    return (
        <AppLayout>
            <PageTitle title={t('flashcards.title')} />
            <div className="mx-auto max-w-6xl space-y-8">
                <Heading as="h1">
                    Practica con estas FlashCards para mejorar tu nivel ☝️☝️
                </Heading>
                <div className="aspect-video w-full overflow-hidden rounded-xl">
                    <iframe
                        src="https://www.youtube.com/embed/js3aTbATSN4"
                        title="Practica con estas FlashCards para mejorar tu nivel"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        referrerPolicy="origin"
                        className="h-full w-full"
                    />
                </div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                    {t('flashcards.chooseSet')}
                </h1>

                {/* Filters */}
                <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <FilterChip
                            active={isActive({ type: 'all' })}
                            onClick={() => setFilter({ type: 'all' })}
                        >
                            {t('flashcards.all')}
                        </FilterChip>
                        {levels.map((level) => (
                            <FilterChip
                                key={level}
                                active={isActive({
                                    type: 'level',
                                    value: level,
                                })}
                                onClick={() =>
                                    setFilter({ type: 'level', value: level })
                                }
                            >
                                {level}
                            </FilterChip>
                        ))}
                    </div>
                    {themes.length > 0 ? (
                        <div className="flex flex-wrap items-center gap-2">
                            {themes.map((theme) => (
                                <FilterChip
                                    key={theme}
                                    active={isActive({
                                        type: 'theme',
                                        value: theme,
                                    })}
                                    onClick={() =>
                                        setFilter({
                                            type: 'theme',
                                            value: theme,
                                        })
                                    }
                                >
                                    {theme}
                                </FilterChip>
                            ))}
                        </div>
                    ) : null}
                </div>

                {/* Sets grid */}
                {filteredSets.length === 0 ? (
                    <EmptyState
                        icon={Layers}
                        description={t('flashcards.noSets')}
                    />
                ) : (
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {filteredSets.map((set) => (
                            <GlassCard
                                key={set.id}
                                as="link"
                                href={`/flashcards/${set.slug}/`}
                                className="group overflow-hidden"
                            >
                                <div className="bg-theme-primary/10 relative flex aspect-[16/7] items-center justify-center">
                                    <GraduationCap className="text-theme-primary-text h-12 w-12 opacity-70 transition-transform duration-300 group-hover:scale-110" />
                                    <span className="from-theme-primary to-theme-secondary absolute inset-x-0 bottom-0 h-1.5 bg-gradient-to-r" />
                                </div>
                                <div className="space-y-2 p-4">
                                    <div className="flex items-center gap-2">
                                        {set.level ? (
                                            <span className="bg-theme-primary text-theme-on-primary rounded px-2 py-0.5 text-xs font-bold">
                                                {set.level}
                                            </span>
                                        ) : null}
                                        <span className="text-xs text-gray-500 dark:text-neutral-400">
                                            {t('flashcards.cards', {
                                                count: set.card_count,
                                            })}
                                        </span>
                                    </div>
                                    <h3 className="line-clamp-2 font-semibold text-gray-800 dark:text-white">
                                        {set.title}
                                    </h3>
                                    {set.theme ? (
                                        <p className="text-xs text-gray-500 dark:text-neutral-400">
                                            {set.theme}
                                        </p>
                                    ) : null}
                                </div>
                            </GlassCard>
                        ))}
                    </div>
                )}
            </div>
        </AppLayout>
    );
};

const Index: React.FC<Props> = () => {
    return (
        <ThemeWrapper>
            <IndexContent />
        </ThemeWrapper>
    );
};

export default Index;
